// packages/utils/src/novel-scoring.ts
// FRAME v3 — Proprietary scoring dimensions
// These computations use standard pixel/landmark data but combine them
// in ways no existing product or paper does. This is the licensing moat.

import type {
  ColorHarmonyResult, ColorRelationship, DepthCues,
  AuthenticitySignals, EmotionalResonanceResult, NovelDimensionScores,
  NovelWeightProfile, GazeResult, TemporalPeakResult,
} from '@frame/scoring-types';
import { HARMONY_MODE_WEIGHTS } from '@frame/scoring-types';

// ─── Color Harmony (Itten's Color Theory) ───────────────────
// Input: array of dominant hues (0-360), saturations (0-1), values (0-1)
// from k-means clustering on HSV pixel data (5-7 clusters)

// Patent: Novel dimension — Color Harmony scoring via Itten's color theory.
// Application to photo curation scoring is novel (Nishiyama 2011 is color harmony detection only).
export function analyzeColorHarmony(
  dominantColors: Array<{ h: number; s: number; v: number; weight: number }>,
): ColorHarmonyResult {
  if (dominantColors.length < 2) {
    return { relationship: 'monochromatic', confidence: 0.8, dominantHues: dominantColors.map(c => c.h), saturationVariance: 0, valueVariance: 0, score: 6.0 };
  }

  const hues = dominantColors.filter(c => c.s > 0.15).map(c => c.h);
  if (hues.length < 2) {
    return { relationship: 'monochromatic', confidence: 0.9, dominantHues: hues, saturationVariance: variance(dominantColors.map(c => c.s)), valueVariance: variance(dominantColors.map(c => c.v)), score: 5.5 };
  }

  // Check each relationship type
  const checks: Array<{ type: ColorRelationship; score: number; confidence: number }> = [
    checkComplementary(hues),
    checkAnalogous(hues),
    checkTriadic(hues),
    checkSplitComplementary(hues),
    checkMonochromatic(hues, dominantColors.map(c => c.s)),
  ];

  // Best match
  const best = checks.reduce((a, b) => a.score > b.score ? a : b);

  // Saturation coherence bonus — photos where saturation levels are intentional score higher
  const satVar = variance(dominantColors.map(c => c.s));
  const satBonus = satVar < 0.05 ? 1.0 : satVar < 0.15 ? 0.5 : 0;

  // Value range bonus — good tonal range improves harmony
  const values = dominantColors.map(c => c.v);
  const valueRange = Math.max(...values) - Math.min(...values);
  const valueBonus = valueRange > 0.3 && valueRange < 0.8 ? 0.5 : 0;

  return {
    relationship: best.type,
    confidence: best.confidence,
    dominantHues: hues,
    saturationVariance: satVar,
    valueVariance: variance(values),
    score: Math.min(10, best.score + satBonus + valueBonus),
  };
}

/**
 * Apply mode-specific harmony relationship weights.
 * E.g., complementary palettes score higher in Instagram mode (1.3x) than Real Estate (1.0x).
 */
export function applyHarmonyModeWeight(
  harmonyResult: ColorHarmonyResult,
  mode: string,
): number {
  const modeWeights = HARMONY_MODE_WEIGHTS[mode] ?? HARMONY_MODE_WEIGHTS.balanced;
  const multiplier = modeWeights[harmonyResult.relationship] ?? 1.0;
  return Math.min(10, harmonyResult.score * multiplier);
}

function hueDist(a: number, b: number): number {
  const d = Math.abs(a - b);
  return Math.min(d, 360 - d);
}

function checkComplementary(hues: number[]) {
  let bestScore = 0;
  for (let i = 0; i < hues.length; i++) {
    for (let j = i + 1; j < hues.length; j++) {
      const dist = hueDist(hues[i], hues[j]);
      const closeness = 1 - Math.abs(dist - 180) / 30; // how close to 180°
      if (closeness > bestScore) bestScore = closeness;
    }
  }
  return { type: 'complementary' as ColorRelationship, score: bestScore > 0.5 ? 5 + bestScore * 4 : 3, confidence: bestScore };
}

function checkAnalogous(hues: number[]) {
  const sorted = [...hues].sort((a, b) => a - b);
  let maxSpan = 0;
  for (let i = 0; i < sorted.length; i++) {
    for (let j = i + 1; j < sorted.length; j++) {
      maxSpan = Math.max(maxSpan, hueDist(sorted[i], sorted[j]));
    }
  }
  const isAnalogous = maxSpan < 60;
  const score = isAnalogous ? 6 + (1 - maxSpan / 60) * 3 : 3;
  return { type: 'analogous' as ColorRelationship, score, confidence: isAnalogous ? 0.8 : 0.3 };
}

function checkTriadic(hues: number[]) {
  if (hues.length < 3) return { type: 'triadic' as ColorRelationship, score: 3, confidence: 0.1 };
  // Check if any 3 hues form ~120° spacing
  let bestScore = 0;
  for (let i = 0; i < hues.length; i++) {
    for (let j = i + 1; j < hues.length; j++) {
      for (let k = j + 1; k < hues.length; k++) {
        const d1 = hueDist(hues[i], hues[j]);
        const d2 = hueDist(hues[j], hues[k]);
        const d3 = hueDist(hues[i], hues[k]);
        const avgDev = (Math.abs(d1 - 120) + Math.abs(d2 - 120) + Math.abs(d3 - 120)) / 3;
        const s = 1 - avgDev / 60;
        if (s > bestScore) bestScore = s;
      }
    }
  }
  return { type: 'triadic' as ColorRelationship, score: bestScore > 0.4 ? 5 + bestScore * 4 : 3, confidence: bestScore };
}

function checkSplitComplementary(hues: number[]) {
  if (hues.length < 3) return { type: 'split-complementary' as ColorRelationship, score: 3, confidence: 0.1 };
  let bestScore = 0;
  for (const base of hues) {
    const comp = (base + 180) % 360;
    const split1 = (comp + 30) % 360;
    const split2 = (comp - 30 + 360) % 360;
    const others = hues.filter(h => h !== base);
    const match1 = Math.min(...others.map(h => hueDist(h, split1)));
    const match2 = Math.min(...others.map(h => hueDist(h, split2)));
    const s = 1 - (match1 + match2) / 120;
    if (s > bestScore) bestScore = s;
  }
  return { type: 'split-complementary' as ColorRelationship, score: bestScore > 0.4 ? 5 + bestScore * 4 : 3, confidence: bestScore };
}

function checkMonochromatic(hues: number[], saturations: number[]) {
  // Circular hue spread — handles wrapping around 0°/360°
  const sortedHues = [...hues].sort((a, b) => a - b);
  let maxGap = 0;
  for (let i = 0; i < sortedHues.length - 1; i++) {
    maxGap = Math.max(maxGap, sortedHues[i + 1] - sortedHues[i]);
  }
  if (sortedHues.length > 0) {
    maxGap = Math.max(maxGap, 360 - sortedHues[sortedHues.length - 1] + sortedHues[0]);
  }
  const circularSpread = 360 - maxGap;
  const isMono = circularSpread < 30;
  const satRange = Math.max(...saturations) - Math.min(...saturations);
  // Monochromatic is strong when there's saturation variation within one hue
  const score = isMono ? 5 + satRange * 4 : 3;
  return { type: 'monochromatic' as ColorRelationship, score, confidence: isMono ? 0.9 : 0.2 };
}

// ─── Depth Perception ───────────────────────────────────────
// Input: blur map variance, atmospheric haze estimate, object size ratios

// Patent: Novel dimension — Depth perception scoring via multi-cue analysis.
// Combines blur gradient, atmospheric perspective, size constancy, overlap, foreground, and texture gradient.
export function analyzeDepth(cues: DepthCues): number {
  const weights = { blur: 0.25, atmos: 0.20, size: 0.15, overlap: 0.15, fg: 0.15, texture: 0.10 };
  const raw =
    cues.blurGradient * weights.blur +
    cues.atmosphericPerspective * weights.atmos +
    cues.sizeConstancy * weights.size +
    cues.overlappingForms * weights.overlap +
    cues.foregroundPresence * weights.fg +
    cues.textureGradient * weights.texture;
  return Math.min(10, raw * 10);
}

// ─── Authenticity Index ─────────────────────────────────────
// Higher = more natural/authentic. Penalizes over-processing.

// Patent: Novel dimension — Authenticity index.
// Detects over-processing (HDR artifacts, oversaturation, filters) distinct from technical distortion.
// High novelty — no prior art for authenticity as a continuous scoring axis in photo curation.
export function analyzeAuthenticity(signals: Omit<AuthenticitySignals, 'score'>): AuthenticitySignals {
  // Each signal is 0-1 where higher = more artificial
  // Weights calibrated for raw 0-1 severity inputs (not threshold-normalized)
  const penalties =
    signals.hdrArtifacts * 4.0 +
    signals.oversaturation * 3.5 +
    signals.noisePatterns * 2.5 +
    (signals.filterDetected ? 1.5 : 0) +
    signals.compressionArtifacts * 1.5 +
    signals.unnaturalSharpening * 2.5;
  const score = Math.max(0, 10 - penalties);
  return { ...signals, score };
}

// ─── Subject-Environment Harmony ────────────────────────────
// How well the subject "belongs" in the scene

// Patent: Novel dimension — Subject-environment harmony scoring.
// Evaluates how well the subject "belongs" in the scene via color relationship + proportion analysis.
export function analyzeSubjectEnvironment(
  subjectAvgColor: { h: number; s: number; v: number },
  backgroundAvgColor: { h: number; s: number; v: number },
  subjectArea: number, // 0-1 proportion of frame
  faceCount: number,
): number {
  // Color temperature match between subject and background
  const tempMatch = 1 - Math.abs(subjectAvgColor.v - backgroundAvgColor.v);

  // Complementary or analogous relationship
  const hueRelation = hueDist(subjectAvgColor.h, backgroundAvgColor.h);
  const hueScore = hueRelation < 30 ? 0.8 : // analogous
                   Math.abs(hueRelation - 180) < 30 ? 0.9 : // complementary
                   hueRelation < 60 ? 0.6 : 0.3; // other

  // Subject should be 10-40% of frame for good composition
  const sizeScore = subjectArea >= 0.10 && subjectArea <= 0.40 ? 1.0 :
                    subjectArea >= 0.05 && subjectArea <= 0.60 ? 0.6 : 0.3;

  // Saturation balance
  const satBalance = 1 - Math.abs(subjectAvgColor.s - backgroundAvgColor.s) * 0.5;

  const raw = tempMatch * 0.3 + hueScore * 0.3 + sizeScore * 0.2 + satBalance * 0.2;
  return Math.min(10, raw * 10);
}

// ─── Emotional Resonance ────────────────────────────────────

// Patent: Novel dimension — Emotional resonance scoring.
// Color psychology + expression analysis + visual tension + negative space → emotional impact score.
export function analyzeEmotionalResonance(
  colorTemp: 'warm' | 'cool' | 'neutral',
  contrastLevel: number, // 0-1
  expressionValence: number, // -1 to 1 (negative to positive)
  expressionIntensity: number, // 0-1
  visualTension: number, // 0-1 (leading lines, dynamic angles)
  negativeSpaceRatio: number, // 0-1
): EmotionalResonanceResult {
  // Color psychology contribution
  const warmthIndex = colorTemp === 'warm' ? 0.8 : colorTemp === 'cool' ? 0.4 : 0.6;

  // High contrast = drama = emotional
  const contrastDrama = contrastLevel;

  // Expression contributes when faces present
  const expressionScore = expressionIntensity * (0.5 + Math.abs(expressionValence) * 0.5);

  // Tension from composition (diagonal lines, implied motion)
  const tensionScore = visualTension * 0.8;

  // Negative space can be powerful
  const spaceScore = negativeSpaceRatio > 0.3 && negativeSpaceRatio < 0.7 ? 0.7 : 0.4;

  const raw =
    warmthIndex * 0.15 +
    contrastDrama * 0.20 +
    expressionScore * 0.30 +
    tensionScore * 0.20 +
    spaceScore * 0.15;

  const score = Math.min(10, raw * 12); // Scale to 0-10 range

  const tempEmotion = colorTemp === 'warm' && contrastLevel > 0.6 ? 'dramatic' :
                      colorTemp === 'warm' ? 'warm-intimate' :
                      colorTemp === 'cool' ? 'cool-calm' : 'neutral';

  return {
    warmthIndex,
    contrastDrama,
    colorTemperatureEmotion: tempEmotion,
    expressionIntensity,
    expressionValence,
    microExpressionDetected: expressionIntensity > 0.3 && expressionIntensity < 0.6,
    visualTension,
    negativeSpace: negativeSpaceRatio,
    score,
  };
}

// ─── Gaze Dynamics ─────────────────────────────────────────
// Input: GazeResult from face detection pipeline

// Patent: Novel dimension — Gaze dynamics scoring.
// Eye contact quality, group engagement, blink detection, gaze directness.
// No prior art for gaze dynamics as a continuous scoring axis in photo curation.
export function analyzeGazeDynamics(gazeResult: GazeResult): number {
  // Eye contact with camera (0 = direct, higher = averted)
  const eyeContactScore = gazeResult.eyeContactScore / 10; // normalize to 0-1

  // Group engagement (are people looking at each other?)
  const groupScore = gazeResult.groupEngagement / 10; // normalize to 0-1

  // Blink penalty
  const blinkFree = gazeResult.blinkDetected ? 0.3 : 1.0;

  // Squint penalty (mild squint is natural, heavy squint is bad)
  const squintFree = 1 - gazeResult.squintLevel * 0.7;

  // Gaze directness — how engaged are the faces overall
  const engagedCount = gazeResult.gazeDirections.filter(g => g.isEngaged).length;
  const totalFaces = gazeResult.gazeDirections.length || 1;
  const gazeDirectness = engagedCount / totalFaces;

  const raw =
    eyeContactScore * 0.30 +
    groupScore * 0.25 +
    blinkFree * 0.20 +
    squintFree * 0.15 +
    gazeDirectness * 0.10;

  return Math.min(10, raw * 10);
}

// ─── Temporal Peak Detection ───────────────────────────────
// Input: TemporalPeakResult from burst sequence analysis

// Patent: Novel dimension — Temporal peak scoring.
// Best-moment detection in burst/series: expression apex, blink-free, motion sharpness.
// No prior art for temporal peak as scoring axis (Apple's "best shot" is binary, not scored).
export function analyzeTemporalPeak(burstResult: TemporalPeakResult): number {
  // Expression at peak (smile apex, action peak)
  const expressionScore = burstResult.expressionPeakScore;

  // No blinks in this frame
  const blinkScore = burstResult.blinkFreeScore;

  // Sharpest frame (least motion blur)
  const sharpnessScore = burstResult.motionBlurScore;

  // Is this THE peak frame?
  const peakBonus = burstResult.isPeakFrame ? 0.15 : 0;

  const raw =
    expressionScore * 0.40 +
    blinkScore * 0.30 +
    sharpnessScore * 0.30 +
    peakBonus;

  return Math.min(10, raw * 10);
}

// ─── Novel Composite ────────────────────────────────────────

// Patent: Claim 1(b) — Novel composite scoring with 7 proprietary dimensions.
// Weighted blend of novel dimensions with mode-specific weight profiles.
export function computeNovelComposite(
  scores: NovelDimensionScores,
  weights: NovelWeightProfile,
): number {
  let weightedSum = 0;
  let totalWeight = 0;

  const pairs: Array<[number | null, number]> = [
    [scores.colorHarmony, weights.colorHarmony],
    [scores.depthPerception, weights.depthPerception],
    [scores.authenticityIndex, weights.authenticityIndex],
    [scores.subjectEnvironment, weights.subjectEnvironment],
    [scores.gazeDynamics, weights.gazeDynamics],
    [scores.emotionalResonance, weights.emotionalResonance],
    [scores.temporalPeak, weights.temporalPeak],
  ];

  for (const [score, weight] of pairs) {
    if (score !== null && weight > 0) {
      weightedSum += score * weight;
      totalWeight += weight;
    }
  }

  return totalWeight > 0 ? Math.round((weightedSum / totalWeight) * 10) / 10 : 0;
}

// Patent: Claim 1(c) — Base-novel composite blending.
// Linear blend with configurable novelBlendRatio per mode.
// Blend base composite with novel composite
export function blendComposites(
  baseComposite: number,
  novelComposite: number,
  novelBlendRatio: number,
): number {
  const baseRatio = 1 - novelBlendRatio;
  return Math.round((baseComposite * baseRatio + novelComposite * novelBlendRatio) * 10) / 10;
}

// ─── Helpers ────────────────────────────────────────────────

function variance(arr: number[]): number {
  if (arr.length === 0) return 0;
  const mean = arr.reduce((s, v) => s + v, 0) / arr.length;
  return arr.reduce((s, v) => s + (v - mean) ** 2, 0) / arr.length;
}
